import { useEffect, useMemo, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import {
  AlertTriangle, ArrowLeft, Bell, Download, Edit3, FileText, LogOut, Plus, Receipt, RefreshCw, Search, Send,
  Sparkles, Trash2,
} from 'lucide-react';
import { toast } from '../components/ui/sonner';
import { Logo } from '../components/Logo';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../components/ui/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../components/ui/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../components/ui/select';
import { Checkbox } from '../components/ui/checkbox';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Label } from '../components/ui/label';
import { getErrorMessage } from '../lib/errors';
import { clearAdminToken } from '../lib/authTokens';
import {
  CURRENCIES, FULFILLMENT_STATUSES, QUANTITY_TIERS, QUOTE_STATUSES, SIZE_GROUPS, computeTotalsPreview,
  displayQuantity, fmtMoney,
} from '../lib/quoteOptions';
import { ADMIN_QUOTES } from '../constants/testIds';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const fmtDate = (iso) => (iso ? new Date(iso).toLocaleString('en-US', { dateStyle: 'medium', timeStyle: 'short' }) : '—');

const STATUS_COLOR = {
  Received: 'text-brand-muted', 'Under Review': 'text-brand-amber', 'Compliance Hold': 'text-red-400',
  Quoted: 'text-brand-orange', Accepted: 'text-brand-green', Rejected: 'text-red-400',
  Invoiced: 'text-brand-amber', Fulfilled: 'text-brand-green', Closed: 'text-brand-muted',
};

const emptyPricing = {
  source: '', unit_price: '', currency: 'USD', freight_included_in_unit_price: false,
  freight_cost: '', insurance_cost: '', shipping_terms: '', destination_port: '', expiration_date: '', payment_terms: '',
};

const selectClass =
  'bg-brand-surface2 border border-brand-border text-brand-text text-sm rounded-md px-3 py-2 focus:outline-none focus:ring-1 focus:ring-brand-orange';

const toDatetimeLocal = (iso) => {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toISOString().slice(0, 16);
};

export default function AdminQuotes() {
  const navigate = useNavigate();
  const [quotes, setQuotes] = useState([]);
  const [invoices, setInvoices] = useState([]);
  const [stats, setStats] = useState({ counts: {}, total: 0 });
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [dueOnly, setDueOnly] = useState(false);

  const [selectedQuote, setSelectedQuote] = useState(null);
  const [noteText, setNoteText] = useState('');
  const [addingNote, setAddingNote] = useState(false);
  const [reminderValue, setReminderValue] = useState('');
  const [savingReminder, setSavingReminder] = useState(false);
  const [statusValue, setStatusValue] = useState('');
  const [savingStatus, setSavingStatus] = useState(false);
  const [pricingForm, setPricingForm] = useState(emptyPricing);
  const [savingPricing, setSavingPricing] = useState(false);
  const [complianceAck, setComplianceAck] = useState(false);
  const [sendingQuote, setSendingQuote] = useState(false);
  const [creatingInvoice, setCreatingInvoice] = useState(false);
  const [invoiceTerms, setInvoiceTerms] = useState('');
  const [paymentRefs, setPaymentRefs] = useState({});

  const [sizesDialogOpen, setSizesDialogOpen] = useState(false);
  const [sizesRows, setSizesRows] = useState([]);
  const [sizesPendingSize, setSizesPendingSize] = useState('');
  const [sizesPendingQty, setSizesPendingQty] = useState('');
  const [sizesTier, setSizesTier] = useState('');
  const [sizesCustomQty, setSizesCustomQty] = useState('');
  const [savingSizes, setSavingSizes] = useState(false);

  const [aiDraftOpen, setAiDraftOpen] = useState(false);
  const [draftingAi, setDraftingAi] = useState(false);
  const [aiSubject, setAiSubject] = useState('');
  const [aiBody, setAiBody] = useState('');
  const [sendingDraft, setSendingDraft] = useState(false);

  const loadQuotes = () => {
    const params = {};
    if (search) params.q = search;
    if (statusFilter !== 'all') params.status = statusFilter;
    if (dueOnly) params.due_reminders = true;
    return axios.get(`${API}/admin/quotes`, { params, withCredentials: true }).then((res) => setQuotes(res.data));
  };

  const loadInvoices = () => axios.get(`${API}/admin/invoices`, { withCredentials: true }).then((res) => setInvoices(res.data));

  const loadStats = () => axios.get(`${API}/admin/quotes/stats`, { withCredentials: true }).then((res) => setStats(res.data));

  const refreshListsAndStats = () => Promise.all([loadQuotes(), loadStats()]);

  useEffect(() => {
    setLoading(true);
    Promise.all([loadQuotes(), loadInvoices(), loadStats()])
      .catch(() => navigate('/admin/login'))
      .finally(() => setLoading(false));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      loadQuotes().catch(() => toast.error('Failed to load quotes'));
    }, 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search, statusFilter, dueOnly]);

  const handleLogout = async () => {
    await axios.post(`${API}/auth/logout`, {}, { withCredentials: true });
    clearAdminToken();
    navigate('/admin/login');
  };

  const openQuote = async (quoteNumber) => {
    try {
      const { data } = await axios.get(`${API}/admin/quotes/${quoteNumber}`, { withCredentials: true });
      setSelectedQuote(data);
      setReminderValue(toDatetimeLocal(data.follow_up_at));
      setStatusValue(data.status);
      setComplianceAck(false);
      setInvoiceTerms(data.pricing?.payment_terms || '');
      setPricingForm(
        data.pricing
          ? { ...emptyPricing, ...data.pricing }
          : { ...emptyPricing, destination_port: data.destination_port }
      );
      setSizesDialogOpen(false);
      setSizesRows(data.sizes || []);
      setSizesTier(data.quantity_tier);
      setSizesCustomQty(data.custom_quantity || '');
      setAiDraftOpen(false);
      setAiSubject('');
      setAiBody('');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to load quote'));
    }
  };

  const refreshSelected = async () => {
    if (!selectedQuote) return;
    const { data } = await axios.get(`${API}/admin/quotes/${selectedQuote.quote_number}`, { withCredentials: true });
    setSelectedQuote(data);
    return data;
  };

  const handleAddNote = async () => {
    if (!noteText.trim()) return;
    setAddingNote(true);
    try {
      await axios.post(`${API}/admin/quotes/${selectedQuote.quote_number}/notes`, { note: noteText }, { withCredentials: true });
      setNoteText('');
      await refreshSelected();
      toast.success('Note added');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to add note'));
    } finally {
      setAddingNote(false);
    }
  };

  const handleSaveReminder = async () => {
    setSavingReminder(true);
    try {
      const iso = reminderValue ? new Date(reminderValue).toISOString() : null;
      await axios.patch(`${API}/admin/quotes/${selectedQuote.quote_number}/reminder`, { follow_up_at: iso }, { withCredentials: true });
      await refreshSelected();
      toast.success('Follow-up reminder saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save reminder'));
    } finally {
      setSavingReminder(false);
    }
  };

  const handleChangeStatus = async (newStatus) => {
    setStatusValue(newStatus);
    setSavingStatus(true);
    try {
      await axios.patch(`${API}/admin/quotes/${selectedQuote.quote_number}/status`, { status: newStatus }, { withCredentials: true });
      await refreshSelected();
      await refreshListsAndStats();
      toast.success(`Status updated to ${newStatus}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update status'));
      setStatusValue(selectedQuote.status);
    } finally {
      setSavingStatus(false);
    }
  };

  const handleSavePricing = async () => {
    if (!pricingForm.unit_price || !pricingForm.shipping_terms || !pricingForm.destination_port || !pricingForm.expiration_date || !pricingForm.payment_terms) {
      toast.error('Unit price, shipping terms, destination port, expiration date and payment terms are required');
      return;
    }
    setSavingPricing(true);
    try {
      const payload = {
        ...pricingForm,
        unit_price: Number(pricingForm.unit_price),
        freight_cost: Number(pricingForm.freight_cost || 0),
        insurance_cost: Number(pricingForm.insurance_cost || 0),
      };
      await axios.put(`${API}/admin/quotes/${selectedQuote.quote_number}/pricing`, payload, { withCredentials: true });
      await refreshSelected();
      await loadQuotes();
      toast.success('Pricing saved');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to save pricing'));
    } finally {
      setSavingPricing(false);
    }
  };

  const handleSendQuote = async () => {
    setSendingQuote(true);
    try {
      await axios.post(
        `${API}/admin/quotes/${selectedQuote.quote_number}/send`,
        { compliance_override_ack: complianceAck, frontend_base_url: window.location.origin },
        { withCredentials: true }
      );
      await refreshSelected();
      await refreshListsAndStats();
      toast.success('Quotation sent to customer');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send quotation'));
    } finally {
      setSendingQuote(false);
    }
  };

  const handleCreateInvoice = async () => {
    setCreatingInvoice(true);
    try {
      await axios.post(
        `${API}/admin/quotes/${selectedQuote.quote_number}/invoice`,
        { payment_terms: invoiceTerms || null, frontend_base_url: window.location.origin },
        { withCredentials: true }
      );
      await refreshSelected();
      await loadInvoices();
      await refreshListsAndStats();
      toast.success('Invoice created and emailed to customer');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to create invoice'));
    } finally {
      setCreatingInvoice(false);
    }
  };

  const handleMarkPaid = async (invoiceNumber) => {
    const ref = (paymentRefs[invoiceNumber] || '').trim();
    if (!ref) {
      toast.error('Enter a payment reference first');
      return;
    }
    try {
      await axios.patch(`${API}/admin/invoices/${invoiceNumber}/payment`, { action: 'mark_paid', payment_reference: ref }, { withCredentials: true });
      await loadInvoices();
      toast.success(`Invoice ${invoiceNumber} marked Paid`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to mark invoice paid'));
    }
  };

  const handleCancelInvoice = async (invoiceNumber) => {
    try {
      await axios.patch(`${API}/admin/invoices/${invoiceNumber}/payment`, { action: 'cancel' }, { withCredentials: true });
      await loadInvoices();
      toast.success(`Invoice ${invoiceNumber} cancelled`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to cancel invoice'));
    }
  };

  const handleFulfillment = async (invoiceNumber, status) => {
    try {
      await axios.patch(`${API}/admin/invoices/${invoiceNumber}/fulfillment`, { fulfillment_status: status }, { withCredentials: true });
      await loadInvoices();
      toast.success(`Fulfillment updated to ${status}`);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update fulfillment'));
    }
  };

  const addSizesRow = () => {
    if (!sizesPendingSize) {
      toast.error('Choose a size first');
      return;
    }
    const qty = parseInt(sizesPendingQty, 10);
    if (!qty || qty <= 0) {
      toast.error('Enter a quantity greater than 0');
      return;
    }
    if (sizesRows.some((r) => r.size === sizesPendingSize)) {
      toast.error('That size is already in the list — remove it first to change the quantity');
      return;
    }
    setSizesRows((prev) => [...prev, { size: sizesPendingSize, quantity: qty }]);
    setSizesPendingSize('');
    setSizesPendingQty('');
  };

  const removeSizesRow = (idx) => setSizesRows((prev) => prev.filter((_, i) => i !== idx));

  const handleSaveSizes = async () => {
    if (sizesRows.length === 0) {
      toast.error('Add at least one size');
      return;
    }
    if (!sizesTier) {
      toast.error('Select a quantity tier');
      return;
    }
    if (sizesTier === 'custom' && (!sizesCustomQty || Number(sizesCustomQty) <= 0)) {
      toast.error('Enter the custom quantity');
      return;
    }
    setSavingSizes(true);
    try {
      await axios.put(
        `${API}/admin/quotes/${selectedQuote.quote_number}/sizes`,
        { sizes: sizesRows, quantity_tier: sizesTier, custom_quantity: sizesTier === 'custom' ? Number(sizesCustomQty) : null },
        { withCredentials: true }
      );
      const updated = await refreshSelected();
      await loadQuotes();
      setSizesTier(updated.quantity_tier);
      setSizesCustomQty(updated.custom_quantity || '');
      setSizesDialogOpen(false);
      toast.success('Sizes & quantity updated');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to update sizes'));
    } finally {
      setSavingSizes(false);
    }
  };

  const handleDraftFollowup = async () => {
    setDraftingAi(true);
    try {
      const { data } = await axios.post(`${API}/admin/quotes/${selectedQuote.quote_number}/followup-draft`, {}, { withCredentials: true });
      setAiSubject(data.subject);
      setAiBody(data.body);
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to generate a draft'));
    } finally {
      setDraftingAi(false);
    }
  };

  const openAiDraft = async () => {
    setAiDraftOpen(true);
    if (!aiSubject && !aiBody) {
      await handleDraftFollowup();
    }
  };

  const handleSendDraft = async () => {
    if (!aiSubject.trim() || !aiBody.trim()) {
      toast.error('Subject and body are required');
      return;
    }
    setSendingDraft(true);
    try {
      await axios.post(
        `${API}/admin/quotes/${selectedQuote.quote_number}/send-followup`,
        { subject: aiSubject, body: aiBody },
        { withCredentials: true }
      );
      await refreshSelected();
      setAiDraftOpen(false);
      toast.success('Follow-up email sent to customer');
    } catch (err) {
      toast.error(getErrorMessage(err, 'Failed to send the follow-up email'));
    } finally {
      setSendingDraft(false);
    }
  };

  const livePreview = useMemo(() => {
    if (!selectedQuote) return null;
    return computeTotalsPreview(selectedQuote, {
      ...pricingForm,
      unit_price: Number(pricingForm.unit_price || 0),
      freight_cost: Number(pricingForm.freight_cost || 0),
      insurance_cost: Number(pricingForm.insurance_cost || 0),
    });
  }, [selectedQuote, pricingForm]);

  const statusOptionsForSelect = QUOTE_STATUSES.filter((s) => s !== 'Quoted');

  return (
    <div className="min-h-screen bg-brand-bg text-brand-text font-body">
      <header className="flex items-center justify-between px-5 sm:px-8 h-20 border-b border-brand-border/60">
        <div className="flex items-center gap-5">
          <Logo />
          <Link to="/admin/dashboard" data-testid={ADMIN_QUOTES.backToOrdersLink} className="inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider text-brand-muted hover:text-brand-orange transition-colors">
            <ArrowLeft className="w-3.5 h-3.5" /> Orders Dashboard
          </Link>
        </div>
        <button type="button" data-testid="admin-quotes-logout-button" onClick={handleLogout}
          className="inline-flex items-center gap-2 font-mono text-xs uppercase tracking-wider text-brand-muted border border-brand-border rounded-full px-4 py-2 hover:border-brand-orange hover:text-brand-orange transition-colors">
          <LogOut className="w-3.5 h-3.5" /> Logout
        </button>
      </header>

      <main className="max-w-7xl mx-auto px-5 sm:px-8 py-10" data-testid={ADMIN_QUOTES.page}>
        <h1 className="font-display font-extrabold uppercase text-3xl text-brand-text">Wholesale Quotes</h1>
        <p className="mt-2 text-brand-muted text-sm">Manage quote requests, pricing, compliance, and invoices.</p>

        {!loading && (
          <div data-testid={ADMIN_QUOTES.funnelWidget} className="mt-8 rounded-xl border border-brand-border bg-brand-surface p-5">
            <p className="font-mono text-[11px] uppercase tracking-wider text-brand-muted mb-3">Pipeline Funnel · {stats.total} total quotes</p>
            <div className="space-y-2">
              {QUOTE_STATUSES.map((s) => {
                const count = stats.counts?.[s] || 0;
                const pct = stats.total ? Math.max((count / stats.total) * 100, count > 0 ? 4 : 0) : 0;
                return (
                  <div key={s} data-testid={ADMIN_QUOTES.funnelBar(s)} className="flex items-center gap-3">
                    <span className="w-28 shrink-0 font-mono text-[11px] uppercase text-brand-muted">{s}</span>
                    <div className="flex-1 h-5 rounded-full bg-brand-surface2 overflow-hidden">
                      <div
                        className={`h-full rounded-full ${s === 'Compliance Hold' || s === 'Rejected' ? 'bg-red-500/70' : s === 'Accepted' || s === 'Fulfilled' ? 'bg-brand-green/70' : 'bg-brand-orange/70'}`}
                        style={{ width: `${pct}%` }}
                      />
                    </div>
                    <span className="w-8 text-right font-mono text-xs text-brand-text">{count}</span>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {loading ? (
          <p className="mt-10 font-mono text-sm text-brand-muted">Loading…</p>
        ) : (
          <Tabs defaultValue="quotes" className="mt-8">
            <TabsList className="bg-brand-surface2 border border-brand-border p-1 rounded-lg h-auto flex-wrap">
              <TabsTrigger value="quotes" data-testid={ADMIN_QUOTES.tabQuotes} className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <FileText className="w-3.5 h-3.5" /> Quotes ({quotes.length})
              </TabsTrigger>
              <TabsTrigger value="invoices" data-testid={ADMIN_QUOTES.tabInvoices} className="font-mono text-xs uppercase tracking-wider text-brand-muted data-[state=active]:bg-brand-orange data-[state=active]:text-white rounded-md px-4 py-2 flex items-center gap-1.5">
                <Receipt className="w-3.5 h-3.5" /> Invoices ({invoices.length})
              </TabsTrigger>
            </TabsList>

            {/* QUOTES TAB */}
            <TabsContent value="quotes" className="mt-6">
              <div className="flex flex-wrap items-center gap-3 mb-4">
                <div className="relative flex-1 min-w-[220px]">
                  <Search className="w-3.5 h-3.5 text-brand-muted absolute left-3 top-1/2 -translate-y-1/2" />
                  <Input data-testid={ADMIN_QUOTES.searchInput} value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search buyer, company, email, destination…" className="pl-9 bg-brand-surface2 border-brand-border text-brand-text" />
                </div>
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger data-testid={ADMIN_QUOTES.statusFilterSelect} className="w-48 bg-brand-surface2 border-brand-border text-brand-text">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">All Statuses</SelectItem>
                    {QUOTE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                  </SelectContent>
                </Select>
                <button
                  type="button"
                  data-testid={ADMIN_QUOTES.dueRemindersToggle}
                  onClick={() => setDueOnly((v) => !v)}
                  className={`inline-flex items-center gap-1.5 font-mono text-xs uppercase tracking-wider rounded-full px-4 py-2 border transition-colors ${
                    dueOnly ? 'bg-brand-amber/15 border-brand-amber text-brand-amber' : 'border-brand-border text-brand-muted hover:border-brand-amber'
                  }`}
                >
                  <Bell className="w-3.5 h-3.5" /> Due Follow-ups
                </button>
              </div>

              <div className="rounded-xl border border-brand-border overflow-hidden" data-testid={ADMIN_QUOTES.table}>
                <Table>
                  <TableHeader>
                    <TableRow className="border-brand-border hover:bg-transparent">
                      {['Quote #', 'Buyer', 'Qty', 'Destination', 'Status', 'Follow-up', 'Date', ''].map((h) => (
                        <TableHead key={h} className="text-brand-muted font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {quotes.length === 0 && (
                      <TableRow className="border-brand-border"><TableCell colSpan={8} className="text-center text-brand-muted py-8">No quote requests match these filters.</TableCell></TableRow>
                    )}
                    {quotes.map((q) => (
                      <TableRow key={q.quote_number} data-testid={ADMIN_QUOTES.row(q.quote_number)} className="border-brand-border hover:bg-brand-surface2/60">
                        <TableCell className="font-mono text-brand-amber text-xs">{q.quote_number}</TableCell>
                        <TableCell className="text-brand-text text-sm">{q.buyer_name}{q.company_name ? <span className="text-brand-muted text-xs"><br />{q.company_name}</span> : null}</TableCell>
                        <TableCell className="text-brand-text text-sm">{displayQuantity(q)}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{q.destination_country}</TableCell>
                        <TableCell className={`text-xs uppercase font-mono ${STATUS_COLOR[q.status] || 'text-brand-muted'}`}>
                          {q.status}
                          {q.compliance_flag && <AlertTriangle className="w-3 h-3 inline-block ml-1.5 text-red-400" />}
                        </TableCell>
                        <TableCell className="text-brand-muted text-xs">{q.follow_up_at ? fmtDate(q.follow_up_at) : '—'}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{fmtDate(q.created_at)}</TableCell>
                        <TableCell>
                          <button type="button" data-testid={ADMIN_QUOTES.viewButton(q.quote_number)} onClick={() => openQuote(q.quote_number)}
                            className="inline-flex items-center gap-1.5 font-mono text-[11px] uppercase tracking-wider text-brand-amber border border-brand-border rounded-full px-3 py-1.5 hover:border-brand-amber transition-colors whitespace-nowrap">
                            View
                          </button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>

            {/* INVOICES TAB */}
            <TabsContent value="invoices" className="mt-6">
              <div className="rounded-xl border border-brand-border overflow-hidden" data-testid={ADMIN_QUOTES.invoicesTable}>
                <Table>
                  <TableHeader>
                    <TableRow className="border-brand-border hover:bg-transparent">
                      {['Invoice #', 'Quote #', 'Buyer', 'Amount', 'Status', 'Fulfillment', 'Payment Ref', 'Date'].map((h) => (
                        <TableHead key={h} className="text-brand-muted font-mono text-[11px] uppercase tracking-wider">{h}</TableHead>
                      ))}
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {invoices.length === 0 && (
                      <TableRow className="border-brand-border"><TableCell colSpan={8} className="text-center text-brand-muted py-8">No invoices issued yet.</TableCell></TableRow>
                    )}
                    {invoices.map((inv) => (
                      <TableRow key={inv.invoice_number} data-testid={ADMIN_QUOTES.invoiceRow(inv.invoice_number)} className="border-brand-border hover:bg-brand-surface2/60">
                        <TableCell className="font-mono text-brand-amber text-xs">{inv.invoice_number}</TableCell>
                        <TableCell className="text-brand-muted text-xs">{inv.quote_number}</TableCell>
                        <TableCell className="text-brand-text text-sm">{inv.buyer_name}</TableCell>
                        <TableCell className="text-brand-text text-sm font-mono">{fmtMoney(inv.amount, inv.currency)}</TableCell>
                        <TableCell className={`text-xs uppercase font-mono ${inv.status === 'Paid' ? 'text-brand-green' : inv.status === 'Cancelled' ? 'text-red-400' : 'text-brand-amber'}`}>{inv.status}</TableCell>
                        <TableCell>
                          <Select value={inv.fulfillment_status} onValueChange={(v) => handleFulfillment(inv.invoice_number, v)}>
                            <SelectTrigger data-testid={ADMIN_QUOTES.fulfillmentSelect(inv.invoice_number)} className="h-8 text-xs w-36 bg-brand-surface2 border-brand-border text-brand-text">
                              <SelectValue />
                            </SelectTrigger>
                            <SelectContent>
                              {FULFILLMENT_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                            </SelectContent>
                          </Select>
                        </TableCell>
                        <TableCell>
                          {inv.status === 'Unpaid' ? (
                            <div className="flex items-center gap-1.5">
                              <Input
                                data-testid={ADMIN_QUOTES.paymentReferenceInput}
                                placeholder="Ref #"
                                value={paymentRefs[inv.invoice_number] || ''}
                                onChange={(e) => setPaymentRefs((p) => ({ ...p, [inv.invoice_number]: e.target.value }))}
                                className="h-8 w-24 text-xs bg-brand-surface2 border-brand-border text-brand-text"
                              />
                              <button type="button" data-testid={ADMIN_QUOTES.markPaidButton(inv.invoice_number)} onClick={() => handleMarkPaid(inv.invoice_number)}
                                className="font-mono text-[10px] uppercase text-brand-green border border-brand-border rounded-full px-2.5 py-1.5 hover:border-brand-green transition-colors whitespace-nowrap">
                                Mark Paid
                              </button>
                              <button type="button" data-testid={ADMIN_QUOTES.cancelInvoiceButton(inv.invoice_number)} onClick={() => handleCancelInvoice(inv.invoice_number)}
                                className="font-mono text-[10px] uppercase text-red-400 border border-brand-border rounded-full px-2.5 py-1.5 hover:border-red-400 transition-colors whitespace-nowrap">
                                Cancel
                              </button>
                            </div>
                          ) : (
                            <span className="text-brand-muted text-xs">{inv.payment_reference || '—'}</span>
                          )}
                        </TableCell>
                        <TableCell className="text-brand-muted text-xs">{fmtDate(inv.created_at)}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
            </TabsContent>
          </Tabs>
        )}
      </main>

      {/* QUOTE DETAIL DIALOG */}
      <Dialog open={!!selectedQuote} onOpenChange={(open) => !open && setSelectedQuote(null)}>
        <DialogContent data-testid={ADMIN_QUOTES.detailDialog} className="bg-brand-surface border-brand-border text-brand-text max-w-2xl max-h-[90vh] overflow-y-auto">
          {selectedQuote && (
            <>
              <DialogHeader>
                <DialogTitle className="text-brand-text font-display uppercase flex items-center gap-2">
                  {selectedQuote.quote_number}
                  <span className={`text-xs font-mono normal-case ${STATUS_COLOR[selectedQuote.status] || 'text-brand-muted'}`}>({selectedQuote.status})</span>
                </DialogTitle>
                <DialogDescription className="text-brand-muted text-sm">
                  {selectedQuote.buyer_name}{selectedQuote.company_name ? `, ${selectedQuote.company_name}` : ''} · {selectedQuote.email} · {selectedQuote.phone}
                </DialogDescription>
              </DialogHeader>

              <div className="space-y-6">
                {/* Shipment summary */}
                <div className="rounded-xl border border-brand-border p-4 text-sm space-y-1.5">
                  <div className="flex items-start justify-between gap-3">
                    <p className="text-brand-text">{selectedQuote.grade} {selectedQuote.tire_type} — <span className="text-brand-amber font-mono">{displayQuantity(selectedQuote)} tires</span></p>
                    <button type="button" data-testid={ADMIN_QUOTES.editSizesButton} onClick={() => setSizesDialogOpen(true)}
                      className="shrink-0 inline-flex items-center gap-1 font-mono text-[11px] uppercase text-brand-muted border border-brand-border rounded-full px-3 py-1 hover:border-brand-amber hover:text-brand-amber transition-colors">
                      <Edit3 className="w-3 h-3" /> Edit
                    </button>
                  </div>
                  <p className="text-brand-muted">Destination: {selectedQuote.destination_country} — Port of {selectedQuote.destination_port}</p>
                  {selectedQuote.shipping_terms_preference && <p className="text-brand-muted">Preferred Terms: {selectedQuote.shipping_terms_preference}</p>}
                  <div className="flex flex-wrap gap-1.5 pt-1">
                    {(selectedQuote.sizes || []).map((s) => (
                      <span key={s.size} className="font-mono text-xs text-brand-text border border-brand-border rounded-full px-2.5 py-1">{s.size} x{s.quantity}</span>
                    ))}
                  </div>
                  {selectedQuote.additional_notes && <p className="text-brand-muted pt-1">Notes: {selectedQuote.additional_notes}</p>}
                </div>

                {/* Compliance */}
                {selectedQuote.compliance_flag && (
                  <div data-testid={ADMIN_QUOTES.complianceBanner} className="flex items-start gap-3 rounded-xl border border-red-500/40 bg-red-500/10 p-4">
                    <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
                    <div>
                      <p className="text-red-300 text-sm font-bold">Compliance Review Required</p>
                      <p className="text-red-300/90 text-xs mt-1">{selectedQuote.compliance_reason}</p>
                    </div>
                  </div>
                )}

                {/* Status */}
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Status</Label>
                  <Select value={statusValue} onValueChange={handleChangeStatus} disabled={savingStatus}>
                    <SelectTrigger data-testid={ADMIN_QUOTES.statusSelect} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {statusOptionsForSelect.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}
                    </SelectContent>
                  </Select>
                  <p className="text-brand-muted text-xs mt-1">Use "Send Quotation" below to move a quote to Quoted status.</p>
                </div>

                {/* Internal notes */}
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Internal Notes</Label>
                  <div data-testid={ADMIN_QUOTES.notesList} className="mt-1.5 space-y-2 max-h-36 overflow-y-auto">
                    {(selectedQuote.internal_notes || []).length === 0 && <p className="text-brand-muted text-xs">No notes yet.</p>}
                    {(selectedQuote.internal_notes || []).map((n, i) => (
                      <div key={i} className="rounded-lg bg-brand-surface2 px-3 py-2 text-xs">
                        <p className="text-brand-text">{n.note}</p>
                        <p className="text-brand-muted mt-1">{n.admin_email} · {fmtDate(n.created_at)}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-2 flex gap-2">
                    <Textarea data-testid={ADMIN_QUOTES.noteInput} rows={2} value={noteText} onChange={(e) => setNoteText(e.target.value)} placeholder="Add an internal note…" className="bg-brand-surface2 border-brand-border text-brand-text" />
                    <button type="button" data-testid={ADMIN_QUOTES.addNoteButton} disabled={addingNote} onClick={handleAddNote}
                      className="shrink-0 font-mono text-xs uppercase text-brand-text border border-brand-border rounded-md px-4 hover:border-brand-amber transition-colors disabled:opacity-50">
                      Add
                    </button>
                  </div>
                </div>

                {/* Follow-up reminder */}
                <div>
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Follow-up Reminder</Label>
                  <div className="mt-1.5 flex gap-2">
                    <Input type="datetime-local" data-testid={ADMIN_QUOTES.reminderInput} value={reminderValue} onChange={(e) => setReminderValue(e.target.value)} className="bg-brand-surface2 border-brand-border text-brand-text" />
                    <button type="button" data-testid={ADMIN_QUOTES.saveReminderButton} disabled={savingReminder} onClick={handleSaveReminder}
                      className="shrink-0 font-mono text-xs uppercase text-brand-text border border-brand-border rounded-md px-4 hover:border-brand-amber transition-colors disabled:opacity-50">
                      Save
                    </button>
                    <button type="button" data-testid={ADMIN_QUOTES.aiDraftButton} onClick={openAiDraft}
                      className="shrink-0 inline-flex items-center gap-1.5 font-mono text-xs uppercase text-brand-amber border border-brand-amber/50 rounded-md px-4 hover:bg-brand-amber/10 transition-colors whitespace-nowrap">
                      <Sparkles className="w-3.5 h-3.5" /> AI Follow-up
                    </button>
                  </div>
                </div>

                {/* Pricing calculator */}
                <div className="rounded-xl border border-brand-border p-4">
                  <Label className="text-brand-muted text-xs uppercase tracking-wider">Pricing Calculator</Label>
                  <div className="mt-3 grid grid-cols-2 gap-3">
                    <div>
                      <Label className="text-brand-muted text-[11px]">Source</Label>
                      <Input data-testid={ADMIN_QUOTES.pricingSourceInput} value={pricingForm.source || ''} onChange={(e) => setPricingForm({ ...pricingForm, source: e.target.value })} className="mt-1 h-9 text-sm bg-brand-surface2 border-brand-border text-brand-text" />
                    </div>
                    <div>
                      <Label className="text-brand-muted text-[11px]">Unit Price *</Label>
                      <Input type="number" step="0.01" data-testid={ADMIN_QUOTES.pricingUnitPriceInput} value={pricingForm.unit_price} onChange={(e) => setPricingForm({ ...pricingForm, unit_price: e.target.value })} className="mt-1 h-9 text-sm bg-brand-surface2 border-brand-border text-brand-text" />
                    </div>
                    <div>
                      <Label className="text-brand-muted text-[11px]">Currency</Label>
                      <Select value={pricingForm.currency} onValueChange={(v) => setPricingForm({ ...pricingForm, currency: v })}>
                        <SelectTrigger data-testid={ADMIN_QUOTES.pricingCurrencySelect} className="mt-1 h-9 text-sm bg-brand-surface2 border-brand-border text-brand-text">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          {CURRENCIES.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label className="text-brand-muted text-[11px]">Shipping Terms *</Label>
                      <Input data-testid={ADMIN_QUOTES.pricingShippingTermsInput} value={pricingForm.shipping_terms || ''} onChange={(e) => setPricingForm({ ...pricingForm, shipping_terms: e.target.value })} placeholder="FOB, CIF…" className="mt-1 h-9 text-sm bg-brand-surface2 border-brand-border text-brand-text" />
                    </div>
                    <div className="col-span-2 flex items-center gap-2">
                      <Checkbox data-testid={ADMIN_QUOTES.pricingFreightIncludedToggle} checked={pricingForm.freight_included_in_unit_price} onCheckedChange={(v) => setPricingForm({ ...pricingForm, freight_included_in_unit_price: !!v })} />
                      <Label className="text-brand-muted text-xs">Freight already included in unit price</Label>
                    </div>
                    <div>
                      <Label className="text-brand-muted text-[11px]">Freight Cost</Label>
                      <Input type="number" step="0.01" disabled={pricingForm.freight_included_in_unit_price} data-testid={ADMIN_QUOTES.pricingFreightCostInput} value={pricingForm.freight_cost} onChange={(e) => setPricingForm({ ...pricingForm, freight_cost: e.target.value })} className="mt-1 h-9 text-sm bg-brand-surface2 border-brand-border text-brand-text disabled:opacity-40" />
                    </div>
                    <div>
                      <Label className="text-brand-muted text-[11px]">Insurance Cost</Label>
                      <Input type="number" step="0.01" data-testid={ADMIN_QUOTES.pricingInsuranceCostInput} value={pricingForm.insurance_cost} onChange={(e) => setPricingForm({ ...pricingForm, insurance_cost: e.target.value })} className="mt-1 h-9 text-sm bg-brand-surface2 border-brand-border text-brand-text" />
                    </div>
                    <div>
                      <Label className="text-brand-muted text-[11px]">Destination Port *</Label>
                      <Input data-testid={ADMIN_QUOTES.pricingDestinationPortInput} value={pricingForm.destination_port || ''} onChange={(e) => setPricingForm({ ...pricingForm, destination_port: e.target.value })} className="mt-1 h-9 text-sm bg-brand-surface2 border-brand-border text-brand-text" />
                    </div>
                    <div>
                      <Label className="text-brand-muted text-[11px]">Expiration Date *</Label>
                      <Input type="date" data-testid={ADMIN_QUOTES.pricingExpirationInput} value={pricingForm.expiration_date || ''} onChange={(e) => setPricingForm({ ...pricingForm, expiration_date: e.target.value })} className="mt-1 h-9 text-sm bg-brand-surface2 border-brand-border text-brand-text" />
                    </div>
                    <div className="col-span-2">
                      <Label className="text-brand-muted text-[11px]">Payment Terms *</Label>
                      <Textarea rows={2} data-testid={ADMIN_QUOTES.pricingPaymentTermsInput} value={pricingForm.payment_terms || ''} onChange={(e) => setPricingForm({ ...pricingForm, payment_terms: e.target.value })} className="mt-1 text-sm bg-brand-surface2 border-brand-border text-brand-text" />
                    </div>
                  </div>

                  {livePreview && (
                    <div data-testid={ADMIN_QUOTES.liveTotalPreview} className="mt-4 rounded-lg bg-brand-surface2 px-4 py-3 flex items-center justify-between text-sm">
                      <span className="text-brand-muted">{livePreview.quantity} x {fmtMoney(livePreview.unitPrice, livePreview.currency)} + freight {fmtMoney(livePreview.freight, livePreview.currency)} + ins. {fmtMoney(livePreview.insurance, livePreview.currency)}</span>
                      <span className="text-brand-orange font-mono font-bold">{fmtMoney(livePreview.total, livePreview.currency)}</span>
                    </div>
                  )}

                  <button type="button" data-testid={ADMIN_QUOTES.savePricingButton} disabled={savingPricing} onClick={handleSavePricing}
                    className="mt-4 w-full font-display font-bold uppercase text-sm text-white bg-brand-orange hover:bg-brand-orangeHover rounded-full py-2.5 transition-colors disabled:opacity-60">
                    {savingPricing ? 'Saving…' : 'Save Pricing'}
                  </button>
                </div>

                {/* Send + download + invoice actions */}
                <div className="flex flex-wrap gap-3">
                  {selectedQuote.compliance_flag && (
                    <div className="flex items-center gap-2 w-full">
                      <Checkbox data-testid={ADMIN_QUOTES.complianceOverrideCheckbox} checked={complianceAck} onCheckedChange={(v) => setComplianceAck(!!v)} />
                      <Label className="text-brand-muted text-xs">I have manually reviewed this destination for export compliance</Label>
                    </div>
                  )}
                  <button type="button" data-testid={ADMIN_QUOTES.sendQuoteButton} disabled={sendingQuote || !selectedQuote.pricing || (selectedQuote.compliance_flag && !complianceAck)} onClick={handleSendQuote}
                    className="flex-1 inline-flex items-center justify-center gap-2 font-display font-bold uppercase text-sm text-white bg-brand-orange hover:bg-brand-orangeHover rounded-full px-5 py-2.5 transition-colors disabled:opacity-50">
                    <Send className="w-4 h-4" /> {sendingQuote ? 'Sending…' : 'Send Quotation'}
                  </button>
                  <a href={`${API}/admin/quotes/${selectedQuote.quote_number}/pdf`} target="_blank" rel="noopener noreferrer" data-testid={ADMIN_QUOTES.downloadQuotePdfButton}
                    className="inline-flex items-center justify-center gap-2 font-mono text-xs uppercase text-brand-text border border-brand-border rounded-full px-5 py-2.5 hover:border-brand-amber transition-colors">
                    <Download className="w-4 h-4" /> Quote PDF
                  </a>
                </div>

                {selectedQuote.status === 'Accepted' && !selectedQuote.invoice_number && (
                  <div className="rounded-xl border border-brand-border p-4 space-y-3">
                    <Label className="text-brand-muted text-xs uppercase tracking-wider">Create Invoice</Label>
                    <Textarea rows={2} value={invoiceTerms} onChange={(e) => setInvoiceTerms(e.target.value)} placeholder="Payment terms (defaults to quote's payment terms)" className="bg-brand-surface2 border-brand-border text-brand-text text-sm" />
                    <button type="button" data-testid={ADMIN_QUOTES.createInvoiceButton} disabled={creatingInvoice} onClick={handleCreateInvoice}
                      className="w-full font-display font-bold uppercase text-sm text-white bg-brand-orange hover:bg-brand-orangeHover rounded-full py-2.5 transition-colors disabled:opacity-60">
                      {creatingInvoice ? 'Creating…' : 'Create & Send Invoice'}
                    </button>
                  </div>
                )}

                {selectedQuote.invoice_number && (
                  <p className="text-brand-muted text-xs text-center">
                    Invoice <span className="text-brand-amber font-mono">{selectedQuote.invoice_number}</span> already issued — manage payment in the Invoices tab.
                  </p>
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* EDIT SIZES DIALOG */}
      <Dialog open={sizesDialogOpen} onOpenChange={setSizesDialogOpen}>
        <DialogContent data-testid={ADMIN_QUOTES.sizesEditDialog} className="bg-brand-surface border-brand-border text-brand-text max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-brand-text font-display uppercase">Edit Sizes &amp; Quantity</DialogTitle>
            <DialogDescription className="text-brand-muted text-sm">{selectedQuote?.quote_number}</DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">Total Quantity Tier</Label>
              <div className="mt-2 grid grid-cols-4 gap-2">
                {QUANTITY_TIERS.map((t) => (
                  <button
                    key={t.value}
                    type="button"
                    data-testid={ADMIN_QUOTES.sizesEditTierButton(t.value)}
                    onClick={() => setSizesTier(t.value)}
                    className={`rounded-lg border px-2 py-2 text-center transition-colors ${
                      sizesTier === t.value ? 'border-brand-orange bg-brand-orange/10' : 'border-brand-border hover:border-brand-amber'
                    }`}
                  >
                    <p className="font-display font-bold text-brand-text text-sm">{t.label}</p>
                  </button>
                ))}
              </div>
              {sizesTier === 'custom' && (
                <Input type="number" min="1" placeholder="Exact quantity" data-testid={ADMIN_QUOTES.sizesEditCustomQtyInput} value={sizesCustomQty} onChange={(e) => setSizesCustomQty(e.target.value)} className="mt-2 bg-brand-surface2 border-brand-border text-brand-text" />
              )}
            </div>

            <div>
              <Label className="text-brand-muted text-xs uppercase tracking-wider">Sizes</Label>
              <div className="mt-2 flex gap-2">
                <select data-testid={ADMIN_QUOTES.sizesEditSizeSelect} value={sizesPendingSize} onChange={(e) => setSizesPendingSize(e.target.value)} className={`${selectClass} mt-0 flex-1`}>
                  <option value="">Select a size…</option>
                  {SIZE_GROUPS.map((grp) => (
                    <optgroup key={grp.label} label={grp.label}>
                      {grp.sizes.map((s) => <option key={s} value={s}>{s}</option>)}
                    </optgroup>
                  ))}
                </select>
                <Input type="number" min="1" placeholder="Qty" data-testid={ADMIN_QUOTES.sizesEditQtyInput} value={sizesPendingQty} onChange={(e) => setSizesPendingQty(e.target.value)} className="bg-brand-surface2 border-brand-border text-brand-text w-24" />
                <button type="button" onClick={addSizesRow} data-testid={ADMIN_QUOTES.sizesEditAddButton}
                  className="inline-flex items-center justify-center gap-1 border border-brand-border hover:border-brand-amber text-brand-text font-mono text-xs uppercase rounded-md px-3 transition-colors whitespace-nowrap">
                  <Plus className="w-3.5 h-3.5" /> Add
                </button>
              </div>
              {sizesRows.length > 0 && (
                <div className="mt-3 rounded-lg border border-brand-border divide-y divide-brand-border/60 max-h-48 overflow-y-auto">
                  {sizesRows.map((r, i) => (
                    <div key={r.size} data-testid={ADMIN_QUOTES.sizesEditRow(i)} className="flex items-center justify-between px-3 py-2">
                      <span className="font-mono text-sm text-brand-text">{r.size}</span>
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-sm text-brand-amber">x{r.quantity}</span>
                        <button type="button" onClick={() => removeSizesRow(i)} data-testid={ADMIN_QUOTES.sizesEditRemoveButton(i)} className="text-brand-muted hover:text-red-400 transition-colors">
                          <Trash2 className="w-3.5 h-3.5" />
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>

            <button type="button" data-testid={ADMIN_QUOTES.sizesEditSaveButton} disabled={savingSizes} onClick={handleSaveSizes}
              className="w-full font-display font-bold uppercase text-sm text-white bg-brand-orange hover:bg-brand-orangeHover rounded-full py-2.5 transition-colors disabled:opacity-60">
              {savingSizes ? 'Saving…' : 'Save Sizes & Quantity'}
            </button>
          </div>
        </DialogContent>
      </Dialog>

      {/* AI FOLLOW-UP DRAFT DIALOG */}
      <Dialog open={aiDraftOpen} onOpenChange={setAiDraftOpen}>
        <DialogContent data-testid={ADMIN_QUOTES.aiDraftDialog} className="bg-brand-surface border-brand-border text-brand-text max-w-lg">
          <DialogHeader>
            <DialogTitle className="text-brand-text font-display uppercase flex items-center gap-2">
              <Sparkles className="w-4 h-4 text-brand-amber" /> AI Follow-up Draft
            </DialogTitle>
            <DialogDescription className="text-brand-muted text-sm">
              Drafted for {selectedQuote?.quote_number} — review and edit before sending to {selectedQuote?.email}.
            </DialogDescription>
          </DialogHeader>
          {draftingAi ? (
            <p className="font-mono text-sm text-brand-muted py-8 text-center">Drafting…</p>
          ) : (
            <div className="space-y-3">
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Subject</Label>
                <Input data-testid={ADMIN_QUOTES.aiDraftSubjectInput} value={aiSubject} onChange={(e) => setAiSubject(e.target.value)} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
              </div>
              <div>
                <Label className="text-brand-muted text-xs uppercase tracking-wider">Body</Label>
                <Textarea rows={6} data-testid={ADMIN_QUOTES.aiDraftBodyInput} value={aiBody} onChange={(e) => setAiBody(e.target.value)} className="mt-1.5 bg-brand-surface2 border-brand-border text-brand-text" />
              </div>
              <div className="flex gap-3">
                <button type="button" data-testid={ADMIN_QUOTES.aiDraftRegenerateButton} onClick={handleDraftFollowup} disabled={draftingAi}
                  className="inline-flex items-center gap-1.5 font-mono text-xs uppercase text-brand-text border border-brand-border rounded-full px-4 py-2.5 hover:border-brand-amber transition-colors disabled:opacity-50">
                  <RefreshCw className="w-3.5 h-3.5" /> Regenerate
                </button>
                <button type="button" data-testid={ADMIN_QUOTES.aiDraftSendButton} onClick={handleSendDraft} disabled={sendingDraft}
                  className="flex-1 inline-flex items-center justify-center gap-2 font-display font-bold uppercase text-sm text-white bg-brand-orange hover:bg-brand-orangeHover rounded-full py-2.5 transition-colors disabled:opacity-60">
                  <Send className="w-4 h-4" /> {sendingDraft ? 'Sending…' : 'Send to Customer'}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
